import { ChangeEvent, ClipboardEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';

import { appendClasses } from '@helpers/ui';

import './PinInputField.scss';

interface IPinInputFieldProps {
    className?: string;
    length: number;
    defaultValue?: string;
    onChange: (value: string, index?: number) => void;
}

/**
 * Multi-cell OTP / PIN input with iOS SMS autofill support.
 *
 * The first cell advertises `autocomplete="one-time-code"` with
 * `maxLength={length}` so iOS can hand off the whole SMS payload in one
 * event; any multi-char change or paste is distributed across the
 * remaining cells.
 */
const PinInputField = ({ className, length, defaultValue = '', onChange }: IPinInputFieldProps) => {
    const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

    const seed = () => {
        const arr = new Array<string>(length).fill('');
        defaultValue
            .replace(/\D/g, '')
            .slice(0, length)
            .split('')
            .forEach((ch, i) => {
                arr[i] = ch;
            });
        return arr;
    };

    const [values, setValues] = useState<string[]>(seed);

    // Reset when parent clears `defaultValue` (e.g. after a failed code).
    // We don't sync on every change to avoid fighting keystrokes via the
    // parent's onChange → setState round-trip.
    const prevDefault = useRef(defaultValue);
    useEffect(() => {
        if (prevDefault.current && !defaultValue) {
            setValues(new Array(length).fill(''));
        }
        prevDefault.current = defaultValue;
    }, [defaultValue, length]);

    const focusCell = (idx: number) => {
        const clamped = Math.max(0, Math.min(idx, length - 1));
        const el = inputsRef.current[clamped];
        if (el) {
            el.focus();
            el.select?.();
        }
    };

    const emit = (next: string[]) => {
        const firstEmpty = next.findIndex((v) => v === '');
        onChange(next.join(''), firstEmpty === -1 ? length - 1 : firstEmpty);
    };

    const fillFrom = (startIdx: number, raw: string) => {
        const digits = raw.replace(/\D/g, '');
        if (!digits) return;

        setValues((prev) => {
            const next = [...prev];
            let cursor = startIdx;
            for (const ch of digits) {
                if (cursor >= length) break;
                next[cursor] = ch;
                cursor += 1;
            }
            emit(next);
            // Defer focus so React applies the new value props first.
            requestAnimationFrame(() => focusCell(Math.min(cursor, length - 1)));
            return next;
        });
    };

    const handleChange = (idx: number) => (e: ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;

        // iOS SMS autofill / paste arrive as a multi-char value.
        if (raw.length > 1) {
            fillFrom(idx, raw);
            return;
        }

        const digit = raw.replace(/\D/g, '').slice(0, 1);
        setValues((prev) => {
            if (prev[idx] === digit) return prev;
            const next = [...prev];
            next[idx] = digit;
            emit(next);
            return next;
        });

        if (digit && idx < length - 1) {
            focusCell(idx + 1);
        }
    };

    const handleKeyDown = (idx: number) => (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace') {
            e.preventDefault();
            setValues((prev) => {
                const next = [...prev];
                if (next[idx]) {
                    next[idx] = '';
                    emit(next);
                } else if (idx > 0) {
                    next[idx - 1] = '';
                    emit(next);
                    focusCell(idx - 1);
                }
                return next;
            });
        } else if (e.key === 'ArrowLeft' && idx > 0) {
            e.preventDefault();
            focusCell(idx - 1);
        } else if (e.key === 'ArrowRight' && idx < length - 1) {
            e.preventDefault();
            focusCell(idx + 1);
        }
    };

    const handlePaste = (idx: number) => (e: ClipboardEvent<HTMLInputElement>) => {
        const pasted = e.clipboardData.getData('text');
        if (!pasted) return;
        e.preventDefault();
        fillFrom(idx, pasted);
    };

    return (
        <div className={appendClasses(['pin-input-field', className])}>
            <div className="pincode-input-container">
                {Array.from({ length }).map((_, i) => (
                    <input
                        // eslint-disable-next-line react/no-array-index-key
                        key={i}
                        ref={(el) => {
                            inputsRef.current[i] = el;
                        }}
                        type="text"
                        inputMode="numeric"
                        // Only the first cell is `one-time-code` with full
                        // length so iOS can inject the whole SMS payload.
                        autoComplete={i === 0 ? 'one-time-code' : 'off'}
                        maxLength={i === 0 ? length : 1}
                        pattern="[0-9]*"
                        value={values[i] ?? ''}
                        onChange={handleChange(i)}
                        onKeyDown={handleKeyDown(i)}
                        onPaste={handlePaste(i)}
                        onFocus={(e) => e.target.select()}
                        aria-label={`Digit ${i + 1} of ${length}`}
                    />
                ))}
            </div>
        </div>
    );
};

export default PinInputField;
